/**
 * ChatManager - Real-Time In-Game Multiplayer Chat
 * Handles chat message routing, WebRTC peer synchronization, unread counters,
 * audio notifications, and a collapsible/expandable floating UI panel.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { sanitizeUserInput, sanitizeHtml } from '../utils/Sanitizer.js';

function getStorage() {
  return (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
}

export class ChatManager {
  constructor() {
    this.messages = [];
    this.maxMessages = 100;
    this.isExpanded = false;
    this.unreadCount = 0;
    this.hasCustomToggleState = false;

    // Restore expansion preference if saved
    const s = getStorage();
    if (s) {
      try {
        const saved = s.getItem('cc_chat_expanded');
        if (saved !== null) {
          this.isExpanded = saved === 'true';
          this.hasCustomToggleState = true;
        }
      } catch (e) {}
    }
  }

  /**
   * Returns singleton instance on window if available.
   */
  static getInstance() {
    if (typeof window !== 'undefined') {
      if (!window._ccChatManager) {
        window._ccChatManager = new ChatManager();
      }
      return window._ccChatManager;
    }
    if (!ChatManager._instance) {
      ChatManager._instance = new ChatManager();
    }
    return ChatManager._instance;
  }

  /**
   * Cleans and sanitizes raw user message text against HTML injection via DOMPurify.
   * @param {string} str
   * @returns {string}
   */
  static sanitizeText(str) {
    if (typeof str !== 'string') return '';
    return sanitizeUserInput(str);
  }

  /**
   * Formats a unix timestamp into a local 12-hour or 24-hour time string (e.g. "10:45 AM").
   * @param {number} ts
   * @returns {string}
   */
  static formatTime(ts) {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  }

  /**
   * Sends a chat message from the local player to all connected multiplayer peers.
   * @param {string} rawText
   * @param {Object} [context]
   * @returns {Object|null} The created message object, or null if empty
   */
  sendMessage(rawText, context = {}) {
    const text = sanitizeUserInput(rawText);
    if (!text) return null;

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    const mySeat = context.mySeat || (typeof window !== 'undefined' && window.mySeat ? window.mySeat : () => 'p1');
    const mySeatId = (net && net.mySeatId) || mySeat();

    const s = getStorage();
    const rawMyName = (doc && doc.getElementById('optPlayerName') && doc.getElementById('optPlayerName').value.trim())
      || (s ? (s.getItem('cc_player_name') || '').trim() : '')
      || (net && net.isHost ? 'Host' : 'You');
    const myName = sanitizeUserInput(rawMyName) || 'Player';

    const msg = {
      id: 'msg_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      type: 'chat',
      sender: myName,
      seatId: mySeatId,
      text: text,
      time: Date.now(),
      isLocal: true
    };

    this.messages.push(msg);
    if (this.messages.length > this.maxMessages) {
      this.messages.shift();
    }

    // Broadcast message to peers
    if (net) {
      const payload = {
        id: msg.id,
        type: 'chat',
        sender: msg.sender,
        seatId: msg.seatId,
        text: msg.text,
        time: msg.time
      };

      if (net.isHost && net.clients) {
        for (const client of net.clients.values()) {
          if (client.conn && client.conn.open !== false) {
            try { client.conn.send(payload); } catch (e) {}
          }
        }
      } else if (!net.isHost && net.conn && net.conn.open !== false) {
        try { net.conn.send(payload); } catch (e) {}
      }
    }

    this.renderMessages(context);
    this.scrollToBottom(context);

    // Clear input
    if (doc) {
      const inp = doc.getElementById('chatInput');
      if (inp) inp.value = '';
    }

    return msg;
  }

  /**
   * Handles an incoming chat message from a remote peer.
   * @param {Object} msg
   * @param {Object} [context]
   */
  handleIncomingMessage(msg, context = {}) {
    if (!msg || msg.type !== 'chat') return;
    const cleanText = sanitizeUserInput(msg.text);
    if (!cleanText) return;

    // Deduplicate if already present
    if (msg.id && this.messages.some(m => m.id === msg.id)) {
      return;
    }

    const cleanMsg = {
      id: msg.id || ('msg_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7)),
      type: 'chat',
      sender: sanitizeUserInput(msg.sender || 'Player'),
      seatId: msg.seatId || 'p2',
      text: cleanText,
      time: msg.time || Date.now(),
      isLocal: false
    };

    this.messages.push(cleanMsg);
    if (this.messages.length > this.maxMessages) {
      this.messages.shift();
    }

    // If chat is collapsed, increment unread counter
    if (!this.isExpanded) {
      this.unreadCount++;
      this.updateUnreadBadge(context);
    }

    // Play subtle audio notification
    this.playNotificationSound(context);

    this.renderMessages(context);
    this.scrollToBottom(context);
  }

  /**
   * Adds an informative system message (e.g. player connected, camera paused).
   * @param {string} text
   * @param {Object} [context]
   */
  addSystemMessage(text, context = {}) {
    if (!text) return;
    const sysMsg = {
      id: 'sys_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      type: 'chat',
      sender: 'System',
      seatId: 'system',
      text: text,
      time: Date.now(),
      isSystem: true
    };

    this.messages.push(sysMsg);
    if (this.messages.length > this.maxMessages) {
      this.messages.shift();
    }

    this.renderMessages(context);
    this.scrollToBottom(context);
  }

  /**
   * Plays a gentle, pleasant notification chime when an incoming message arrives.
   * @param {Object} [context]
   */
  playNotificationSound(context = {}) {
    try {
      const audioMgr = context.AudioManager || (typeof window !== 'undefined' ? window.audioManager : null);
      if (audioMgr && typeof audioMgr.tone === 'function') {
        audioMgr.tone(587.33, 0.08, 'sine', 0.04); // D5
        setTimeout(() => {
          try { audioMgr.tone(880, 0.12, 'sine', 0.04); } catch (e) {} // A5
        }, 60);
      }
    } catch (e) {}
  }

  /**
   * Toggles the chat panel between expanded and collapsed states.
   * @param {boolean} [forceState]
   * @param {Object} [context]
   * @returns {boolean} New expansion state
   */
  toggleChat(forceState = undefined, context = {}) {
    this.isExpanded = forceState !== undefined ? Boolean(forceState) : !this.isExpanded;
    this.hasCustomToggleState = true;

    if (this.isExpanded) {
      this.unreadCount = 0;
    }

    try {
      const s = getStorage();
      if (s) {
        s.setItem('cc_chat_expanded', this.isExpanded.toString());
      }
    } catch (e) {}

    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (doc) {
      const container = doc.getElementById('chatWidgetContainer');
      if (container) {
        container.classList.toggle('is-expanded', this.isExpanded);
        container.classList.toggle('is-collapsed', !this.isExpanded);
      }

      this.updateUnreadBadge(context);

      if (this.isExpanded) {
        this.scrollToBottom(context);
        const inp = doc.getElementById('chatInput');
        if (inp && typeof inp.focus === 'function') {
          setTimeout(() => { try { inp.focus(); } catch (e) {} }, 50);
        }
      }
    }

    return this.isExpanded;
  }

  /**
   * Updates the unread message counter badge on the collapsed chat pill.
   * @param {Object} [context]
   */
  updateUnreadBadge(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;

    const badge = doc.getElementById('chatUnreadBadge');
    if (badge) {
      if (this.unreadCount > 0 && !this.isExpanded) {
        badge.textContent = this.unreadCount > 99 ? '99+' : this.unreadCount.toString();
        badge.style.display = 'inline-flex';
      } else {
        badge.textContent = '0';
        badge.style.display = 'none';
      }
    }
  }

  /**
   * Renders the chat widget DOM structure into #chatWidgetContainer.
   * @param {Object} [context]
   */
  renderChatWidget(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;

    let container = doc.getElementById('chatWidgetContainer');
    if (!container) {
      container = doc.createElement('div');
      container.id = 'chatWidgetContainer';
      container.className = `chat-widget ${this.isExpanded ? 'is-expanded' : 'is-collapsed'}`;
      doc.body.appendChild(container);
    } else {
      container.className = `chat-widget ${this.isExpanded ? 'is-expanded' : 'is-collapsed'}`;
    }

    container.innerHTML = `
      <!-- Collapsed Floating Pill -->
      <button type="button" class="chat-toggle-pill" id="btnChatTogglePill" title="Expand Match Chat">
        <span class="chat-pill-icon">💬</span>
        <span class="chat-pill-label">Chat</span>
        <span class="chat-unread-badge" id="chatUnreadBadge" style="display: ${this.unreadCount > 0 && !this.isExpanded ? 'inline-flex' : 'none'};">
          ${this.unreadCount > 99 ? '99+' : this.unreadCount}
        </span>
      </button>

      <!-- Expanded Floating Card Panel -->
      <div class="chat-panel" id="chatPanel">
        <div class="chat-header">
          <div class="chat-header-title">
            <span class="chat-header-icon">💬</span>
            <span class="chat-header-text">Match Chat</span>
          </div>
          <div class="chat-header-actions">
            <button type="button" class="btn-chat-collapse" id="btnChatCollapse" title="Collapse Chat">
              ▼ Minimize
            </button>
          </div>
        </div>

        <div class="chat-messages" id="chatMessagesList" role="log" aria-live="polite"></div>

        <form class="chat-input-bar" id="chatForm">
          <input type="text" class="chat-input" id="chatInput" placeholder="Message match…" maxlength="250" autocomplete="off" />
          <button type="submit" class="btn-chat-send" id="btnChatSend" title="Send Message">
            ➤
          </button>
        </form>
      </div>
    `;

    // Bind event listeners
    const pillBtn = container.querySelector('#btnChatTogglePill');
    if (pillBtn) {
      if (typeof pillBtn.addEventListener === 'function') {
        pillBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleChat(true, context);
        });
      } else {
        pillBtn.onclick = (e) => {
          e.preventDefault();
          this.toggleChat(true, context);
        };
      }
    }

    const collapseBtn = container.querySelector('#btnChatCollapse');
    if (collapseBtn) {
      if (typeof collapseBtn.addEventListener === 'function') {
        collapseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleChat(false, context);
        });
      } else {
        collapseBtn.onclick = (e) => {
          e.preventDefault();
          this.toggleChat(false, context);
        };
      }
    }

    const form = container.querySelector('#chatForm');
    const input = container.querySelector('#chatInput');

    if (form && input) {
      if (typeof form.addEventListener === 'function') {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.sendMessage(input.value, context);
        });
      } else {
        form.onsubmit = (e) => {
          e.preventDefault();
          this.sendMessage(input.value, context);
        };
      }

      if (typeof input.addEventListener === 'function') {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.sendMessage(input.value, context);
          }
        });
      } else {
        input.onkeydown = (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.sendMessage(input.value, context);
          }
        };
      }
    }

    this.renderMessages(context);
    this.updateUnreadBadge(context);
  }

  /**
   * Renders the current list of messages inside #chatMessagesList.
   * @param {Object} [context]
   */
  renderMessages(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;

    const list = doc.getElementById('chatMessagesList');
    if (!list) return;

    if (this.messages.length === 0) {
      list.innerHTML = `<div class="chat-empty-state">No messages yet. Say hello! 👋</div>`;
      return;
    }

    list.innerHTML = this.messages.map(m => {
      if (m.isSystem) {
        return `
          <div class="chat-msg chat-msg-system">
            <span class="chat-sys-text">${ChatManager.sanitizeText(m.text)}</span>
          </div>
        `;
      }

      const isLocal = Boolean(m.isLocal);
      const seatClass = m.seatId ? `chat-seat-${m.seatId}` : '';
      const timeStr = ChatManager.formatTime(m.time);

      return `
        <div class="chat-msg ${isLocal ? 'chat-msg-local' : 'chat-msg-remote'} ${seatClass}">
          <div class="chat-msg-header">
            <span class="chat-msg-sender">${ChatManager.sanitizeText(m.sender)}</span>
            <span class="chat-msg-time">${timeStr}</span>
          </div>
          <div class="chat-msg-bubble">
            <div class="chat-msg-text">${ChatManager.sanitizeText(m.text)}</div>
          </div>
        </div>
      `;
    }).join('');
  }

  /**
   * Smoothly scrolls message list to bottom.
   * @param {Object} [context]
   */
  scrollToBottom(context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;

    const list = doc.getElementById('chatMessagesList');
    if (list) {
      setTimeout(() => {
        try { list.scrollTop = list.scrollHeight; } catch (e) {}
      }, 20);
    }
  }

  /**
   * Shows or hides the entire chat widget depending on whether multiple human players are active.
   * @param {boolean} visible
   * @param {Object} [context]
   */
  setChatVisibility(visible, context = {}) {
    const doc = context.document || (typeof document !== 'undefined' ? document : null);
    if (!doc) return;

    let container = doc.getElementById('chatWidgetContainer');
    if (!container && visible) {
      this.renderChatWidget(context);
      container = doc.getElementById('chatWidgetContainer');
    }

    if (container) {
      container.style.display = visible ? 'flex' : 'none';
    }
  }

  /**
   * Clears all chat messages and resets state (e.g. when leaving match to menu).
   * @param {Object} [context]
   */
  clearChat(context = {}) {
    this.messages = [];
    this.unreadCount = 0;
    this.renderMessages(context);
    this.updateUnreadBadge(context);
  }
}
